/**
 * 🗄️ POI Controller (SQLite-powered)
 * 
 * Fast POI lookup API using SQLite index.
 * Falls back to legacy osmium service if SQLite unavailable.
 * 
 * Endpoints:
 * - GET /api/pois/:osmId - Get POI details by OSM ID
 * - GET /api/pois/search?q=query - Search POIs by name
 * - GET /api/pois/category/:category - Get POIs by category
 * - GET /api/pois/random - Get random POIs for discovery
 * - GET /api/pois/stats - Get database statistics
 */
import { FastifyInstance, FastifyReply, FastifyRequest } from 'fastify'
import { poiSqliteService } from './poi-sqlite.service'
import { POIEnrichmentService } from './poi-enrichment.service'
import { createLogger } from '@/common/utils/logger'
import { z } from 'zod'
import { zodToJsonSchema } from 'zod-to-json-schema'

const logger = createLogger('poi-controller')

// --- ZOD SCHEMAS (for validation) ---
const getPoiDetailsSchema = z.object({
  osmId: z.string().regex(/^\d+$/, 'OSM ID must be a number string')
})

const searchQuerySchema = z.object({
  q: z.string().min(2, 'Query must be at least 2 characters'),
  limit: z.coerce.number().min(1).max(100).optional().default(20)
})

const categoryParamSchema = z.object({
  category: z.string().min(1)
})

const limitQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(100).optional().default(20)
})

// --- JSON SCHEMAS (for Fastify) ---
const getPoiDetailsJsonSchema = zodToJsonSchema(getPoiDetailsSchema)
const searchQueryJsonSchema = zodToJsonSchema(searchQuerySchema)
const categoryParamJsonSchema = zodToJsonSchema(categoryParamSchema)
const limitQueryJsonSchema = zodToJsonSchema(limitQuerySchema)

export class POIController {
  private legacyService: POIEnrichmentService
  private useSqlite: boolean = false

  constructor() {
    this.legacyService = new POIEnrichmentService()

    // Initialize SQLite (async, happens in background)
    this.initializeSqlite()
  }

  /**
   * Initialize SQLite asynchronously.
   * Downloads DB from VPS if needed (dev mode).
   * @private
   */
  private async initializeSqlite() {
    this.useSqlite = await poiSqliteService.initialize()

    if (this.useSqlite) {
      logger.info('POI Controller using SQLite (fast mode)')
    } else {
      logger.warn('POI Controller using legacy osmium service (slow mode)')
    }
  }

  /**
   * Register all POI routes.
   * Note: Routes are relative - prefix '/api/pois' is added by the module registration
   */
  registerRoutes(fastify: FastifyInstance) {
    // Get POI by OSM ID
    fastify.get('/:osmId', {
      schema: { params: getPoiDetailsJsonSchema }
    }, this.getPoiDetails.bind(this))

    // Search POIs
    fastify.get('/search', {
      schema: { querystring: searchQueryJsonSchema }
    }, this.searchPois.bind(this))

    // Get POIs by category
    fastify.get('/category/:category', {
      schema: { params: categoryParamJsonSchema, querystring: limitQueryJsonSchema }
    }, this.getByCategory.bind(this))

    // Get random POIs
    fastify.get('/random', {
      schema: { querystring: limitQueryJsonSchema }
    }, this.getRandomPois.bind(this))

    // Get stats
    fastify.get('/stats', this.getStats.bind(this))

    // Health check
    fastify.get('/health', this.healthCheck.bind(this))
  }

  /**
   * GET /api/pois/:osmId
   * Returns POI details by OSM ID.
   */
  async getPoiDetails(request: FastifyRequest, reply: FastifyReply) {
    const { osmId } = request.params as { osmId: string }
    
    try {
      // Try SQLite first (fast)
      if (this.useSqlite) {
        const poi = poiSqliteService.getByOsmId(osmId)
        if (poi) {
          return reply.code(200).send(poi)
        }
      }

      // Fallback to legacy service (slow, with AI enrichment)
      logger.info(`POI ${osmId} not in SQLite, trying legacy service`)
      const poiDetails = await this.legacyService.getEnrichedPoiDetails(osmId)
      
      if (!poiDetails) {
        return reply.code(404).send({ 
          error: 'POI not found',
          message: `No POI found with OSM ID: ${osmId}`
        })
      }

      return reply.code(200).send(poiDetails)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error fetching POI ${osmId}`, { error: error.message })
      return reply.code(500).send({ 
        error: 'Internal server error',
        message: 'Failed to fetch POI details'
      })
    }
  }

  /**
   * GET /api/pois/search?q=query&limit=20
   * Search POIs by name.
   */
  async searchPois(request: FastifyRequest, reply: FastifyReply) {
    const { q, limit } = request.query as { q: string; limit: number }
    
    if (!this.useSqlite) {
      return reply.code(503).send({ 
        error: 'Service unavailable',
        message: 'POI search requires SQLite index. Run build-poi-index.mjs first.'
      })
    }

    try {
      const pois = poiSqliteService.searchByName(q, limit)
      return reply.code(200).send({
        query: q,
        count: pois.length,
        results: pois
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error searching POIs for "${q}"`, { error: error.message })
      return reply.code(500).send({ error: 'Search failed' })
    }
  }

  /**
   * GET /api/pois/category/:category?limit=20
   * Get POIs by category.
   */
  async getByCategory(request: FastifyRequest, reply: FastifyReply) {
    const { category } = request.params as { category: string }
    const { limit } = request.query as { limit: number }
    
    if (!this.useSqlite) {
      return reply.code(503).send({ 
        error: 'Service unavailable',
        message: 'Category browsing requires SQLite index.'
      })
    }

    try {
      const pois = poiSqliteService.getByCategory(category, limit)
      return reply.code(200).send({
        category,
        count: pois.length,
        results: pois
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error fetching category "${category}"`, { error: error.message })
      return reply.code(500).send({ error: 'Failed to fetch category' })
    }
  }

  /**
   * GET /api/pois/random?limit=10
   * Get random high-quality POIs.
   */
  async getRandomPois(request: FastifyRequest, reply: FastifyReply) {
    const { limit } = request.query as { limit: number }
    
    if (!this.useSqlite) {
      return reply.code(503).send({ 
        error: 'Service unavailable',
        message: 'Random POIs requires SQLite index.'
      })
    }

    try {
      const pois = poiSqliteService.getRandom(limit)
      return reply.code(200).send({
        count: pois.length,
        results: pois
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Error fetching random POIs', { error: error.message })
      return reply.code(500).send({ error: 'Failed to fetch random POIs' })
    }
  }

  /**
   * GET /api/pois/stats
   * Get database statistics.
   */
  async getStats(_request: FastifyRequest, reply: FastifyReply) {
    if (!this.useSqlite) {
      return reply.code(503).send({ 
        error: 'Service unavailable',
        message: 'Stats require SQLite index.',
        sqliteAvailable: false
      })
    }

    try {
      const stats = poiSqliteService.getStats()
      return reply.code(200).send({
        sqliteAvailable: true,
        ...stats
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Error fetching stats', { error: error.message })
      return reply.code(500).send({ error: 'Failed to fetch stats' })
    }
  }

  /**
   * GET /api/pois/health
   * Health check endpoint.
   */
  async healthCheck(_request: FastifyRequest, reply: FastifyReply) {
    const sqliteHealthy = poiSqliteService.isHealthy()
    
    return reply.code(sqliteHealthy ? 200 : 503).send({
      status: sqliteHealthy ? 'healthy' : 'degraded',
      sqlite: {
        available: this.useSqlite,
        healthy: sqliteHealthy
      },
      fallback: 'osmium (slow)'
    })
  }
}